import * as _ from 'lodash';
import {FontSize} from "~/enums/fontSize";
import {dateIndexProperties, IndexType, numericIndexProperties} from "~/enums/formElementIndexType";
import {ModelType} from "~/enums/modelType"
import {SearchMatchType} from '~/enums/searchMatchType';
import {ValidationReferenceType} from '~/enums/validationReferenceType';
import Guid from "~/helpers/guid";
import {ObjectHelper} from "~/helpers/objectHelper";
import {GenericType, IGenericTypeable} from '~/interfaces/genericTypeInterfaces';
import {Hash} from "~/interfaces/hashInterfaces";
import {IObjectModelProperty} from "~/interfaces/objectModelInterfaces";
import {IPermittee} from "~/interfaces/permitteeInterfaces";
import {
  IAncestorable,
  IClientIdable,
  ICollectionable,
  IDataCollectionable,
  IDefaultLabelable,
  IDisplayTitle,
  IFormattable,
  IFormElementsInFormula,
  IFormElementType,
  IRepeatable,
  ISortable,
} from "~/interfaces/propertyInterfaces";
import {ComparisonType} from "~/models/conditionalRules/comparisonType";
import {ConditionalFieldDefinition} from "~/models/conditionalRules/conditionalFieldDefinition";
import {TargetType} from "~/models/conditionalRules/targetType";
import {DatetimeMode} from "~/models/datetimeMode";
import ListView from "~/models/listViews/listView";
import {FormElementValidation} from "~/models/navigableValidations/formElementValidation";
import ObjectModel from '~/models/objectModel';
import ObjectModelProperty from '~/models/objectModelProperties/objectModelProperty';
import ObjectModelConditionalFields from '~/models/objectModels/objectModelConditionalFields';
import ObjectModelConditionalRuleSet from '~/models/objectModels/objectModelConditionalRuleSet';
import {RelationType} from "~/models/relationType";
import SecurityMaskableViewer from "~/models/securityMaskableViewer";
import IRepo from "../../../../common/interfaces/repo";
import Validatable from "../../../../common/mixins/validatable";
import ConditionalRule from "../../../../common/models/conditionalRules/conditionalRule";
import IConditionalRuleContainer from "../../../../common/models/conditionalRules/conditionalRuleContainer";
import ConditionalRuleSet from "../../../../common/models/conditionalRules/conditionalRuleSet";
import ConditionalRuleSetFactory from "../../../../common/models/conditionalRules/conditionalRuleSetFactory";
import IConditionalRuleReferencedControl from "../../../../common/models/conditionalRules/referencedControl";
import Repo from "../../../../common/state/modules/repo";
import {OptionRangeType} from "~/enums/optionRangeType";
import numericValidationWithOptionalDecimalPlaces from "../../helpers/numericValidationWithDecimalPlaces";
import Selected from "../../store/modules/selected";
import CharacterCount from "../characterCounts/characterCount";
import FormElementConditionalFields from "../conditionalRules/formElementConditionalFields";
import FormElementConditionalRuleSet from "../conditionalRules/formElementConditionalRuleSet";
import {CurrencyType} from "../currencyType";
import Form, {IForm} from "../form";
import FormSection from "../formSections/formSection";
import FormulaFields from "../formulas/formulaFields";
import {InputFormatType} from "../inputFormatType";
import WorkflowStep from "../workflow/workflowStep";
import ElementContainer from "./elementContainer";
import {Capability} from "./formElementCapability";
import FormElementCapabilityManager from "./formElementCapabilityManager";
import FormElementDefinition from "~/definitions/formElementDefinition";
import {FormElementType} from "~/enums/formElementType";
import {FrameShape} from "~/models/formElements/frameShape";
import TemplateElement from "./templates/templateElement";
import {VerticalAlignment} from "~/enums/verticalAlignment";
import {ReferenceNodeType} from '~/enums/referenceNodeType'
import {IReferenceNodeable} from '~/interfaces/referencePathInterfaces'
import {IModelTypeable} from '~/interfaces/modelInterfaces'
import {IImposter, IImposterFactory, IImposterMap, IImposterOptions} from '~/interfaces/imposterInterfaces'
import {LookupFieldType} from '~/enums/lookupFieldType'
import Imposter from '~/models/imposter'
import DefaultImposterMapFactory from '~/models/imposterMapFactories/defaultImposterMapFactory'
import {commonImposterMap} from '~/models/imposterMapFactories/commonImposterMap'
import {PickListSelectionType} from '~/enums/pickListSelectionType'
import {InstanceHelper} from '~/helpers/instanceHelper'
import {IUniversalValue} from '~/interfaces/universalValueInterfaces'
import { DatetimeType } from '~/models/datetimeType';
import { controlName, label } from '~/utils/formElementUtils'

interface ILookupFieldFormElement {
  lookup_type: number
  reference_data_ids: string[]
  object_model_id: string
  object_model_instance_selection_limit: number
  object_model_search_property_ids: string[]
  object_model_search_match_type: SearchMatchType
  object_model_search_result_label_property_ids: string[]
  list_view_id: string
}

export type IFormElement =
  IConditionalRuleContainer
  & IConditionalRuleReferencedControl
  & IClientIdable
  & IRepeatable
  & IDefaultLabelable
  & IFormElementType
  & IDisplayTitle
  & IAncestorable
  & IFormElementsInFormula
  & IGenericTypeable
  & IReferenceNodeable
  & IModelTypeable
  & ILookupFieldFormElement
  & IImposterFactory
  & IFormattable
  & ICollectionable
  & IDataCollectionable
  & IUniversalValue
  & ISortable
  & {
  form: IForm
  containerScope: FormSection | ElementContainer
  workflowStep: WorkflowStep
  can(duty: Capability): boolean
  is_multiple: boolean
  isUploader: boolean;
}

export default class FormElement extends Validatable(ElementContainer) implements IFormElement, IImposterFactory, IConditionalRuleReferencedControl, IConditionalRuleContainer, IUniversalValue {
  min_value: string;
  default_value: number | string;
  decimal_places: number;
  sort_order: number;
  format_type: InputFormatType;
  format_message: string
  format_pattern: string;
  currency_type: CurrencyType;
  format_numeric_with_commas: boolean;
  is_repeatable: boolean;
  font_size: FontSize;
  vertical_alignment: VerticalAlignment;
  reference_name: string;
  datetime_mode: DatetimeMode;
  min_datetime: string;
  min_datetime_formula: string;
  max_datetime: string;
  max_datetime_formula: string;
  default_datetime_type: DatetimeType;
  default_datetime: string;
  default_datetime_formula: string;
  label_title: string;
  check_by_default: boolean;
  is_readonly: boolean;
  character_count: CharacterCount;
  _elementRestrictions: Array<Array<Capability>>; // intended to be based on child form element sort order
  _sortOrdersToParentTemplate: Array<number>;
  _sortOrdersToTargetChild: Array<number>;
  _restrictionsFromTemplate: Array<Capability>;
  is_multiple: boolean;
  frame_shape: FrameShape;
  frame_width: number;
  frame_height: number;
  allow_image_min_max: boolean;
  image_width_max: number;
  image_width_min: number;
  image_height_max: number;
  image_height_min: number;
  help_text: string;
  licenses: Array<any>;
  lookup_type: LookupFieldType
  reference_data_ids: string[]
  object_model_id: string
  object_model_search_property_ids: string[]
  list_view_id: string
  list_view: ListView | any;
  profile_config_field_id: string;
  value: string;
  max_repeatable_instances: number;
  object_model_for_mappings: { id: string, client_id: string, object_model_properties: IObjectModelProperty[] }
  object_model: any;
  object_model_search_match_type: SearchMatchType;
  object_model_search_result_label_property_ids: Array<string>;
  option_range_type: OptionRangeType;
  checkbox_range_min: number;
  checkbox_range_max: number;
  is_required: boolean;
  placeholder_text: string;
  object_model_instance_selection_limit: number;
  validation_reference_type: number;
  validation_reference_object_model_id: string;
  has_display_label: boolean;
  display_label: string;
  is_formattable: boolean;
  clear_hidden_on_submit: boolean;

  //----- SecurityMaskable
  is_masked: boolean // legacy -- replaced in security maskable module by security_maskable_enabled
  security_maskable_partial_display_character_count: number;
  security_maskable_full_viewers: { [key: string]: IPermittee } = {};
  security_maskable_partial_viewers: { [key: string]: IPermittee } = {};


  get securityMaskingFullViewersArray(): IPermittee[] {
    return ObjectHelper.toArray(this.security_maskable_full_viewers);
  }

  get securityMaskingPartialViewersArray(): IPermittee[] {
    return ObjectHelper.toArray(this.security_maskable_partial_viewers);
  }

  appendSecurityMaskableViewers(json, collectionNameOnParent: "security_maskable_full_viewers" | "security_maskable_partial_viewers"): IPermittee {
    const permittee = new SecurityMaskableViewer(this, json, collectionNameOnParent);
    this.trackCreationDeletion(permittee.client_id, null, permittee.asJson(), permittee);
    return permittee;
  }
  // override concern field for legacy is_masked usage
  get security_maskable_enabled(): boolean {
    return this.is_masked;
  }

  updateFormatAssociatedFields(format): void {
    if (format === InputFormatType.SocialSecurity) {
      this.is_masked = true;
      this.security_maskable_partial_display_character_count = 4;
    }
  }
  //----- SecurityMaskable end

  static repo: IRepo<FormElement> = new Repo<FormElement>('formElements', FormElement);

  except = ['form', 'form_elements', 'createEmbeddedObject', 'licenses', 'object_model'];

  static readonly SETTINGS_METADATA = {
    object_model_search_result_label_property_ids: {
      label: 'Which properties show in the search dropdown options?',
      elementId: 'object-model-search-property-label-property-ids-select',
    },
  };

  constructor() {
    super();
    for (const definition of FormElementConditionalFields.FORM_ELEMENT_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(parent, definition, json, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }

    for (const definition of ObjectModelConditionalFields.VALIDATION_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(parent, definition, json, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }

    this.defineProperty('client_id', Guid.create());
  }

  static getElementIndexType(element: FormElement){
    const elementType = element.elementType;

    if (dateIndexProperties.includes(elementType)) {
      return IndexType.Date;
    } else if (numericIndexProperties.includes(elementType) && element.format_type === InputFormatType.Numeric) {
      return IndexType.Double;
    } else {
      return IndexType.Keyword;
    }
  }

  //Used in certificate-mapping-row-form-element-container-select.vue to keep same interface between form and form
  // elements for certificate mappings
  get getFormElements(): FormElement[] {
    return this.formElementsArrayDeep;
  }

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }

  get hasFormat(): boolean {
    return this.format_type && this.format_type > InputFormatType.None;
  }

  get displayName(): string {
    return this.label_title;
  }

  //For Min Max validation
  get character_count_min() {
    return this.character_count.min
  }
  get character_count_max() {
    return this.character_count.max
  }

  // //******************** VALIDATABLE ********************//
  _validation: FormElementValidation<FormElement> = new FormElementValidation(this);
  // //******************** VALIDATABLE ********************//

  initialize(root: any, parent: ElementContainer, elementType: FormElementType, json: object = {}): this {
    this.rootModel = root;
    this.parent = parent;

    this.defineProperty('element_type', elementType);
    this.defineProperty('sort_order', json['sort_order'] || 0);

    FormElementCapabilityManager.addDefaultProperties(this);

    if (this.isTemplateType) this.constructTemplate();

    for (const definition of FormElementConditionalFields.FORM_ELEMENT_FIELDS) {
      if (definition.defaultConditionalValue instanceof Function) {
        this[definition.fieldName] = definition.defaultConditionalValue(this);
      }
    }

    for (const definition of ObjectModelConditionalFields.VALIDATION_FIELDS) {
      if (definition.defaultConditionalValue instanceof Function) {
        this[definition.fieldName] = definition.defaultConditionalValue(this);
      }
    }

    //add default formula fields
    const formElementFields = FormulaFields.FORM_ELEMENT_FIELDS;
    for (const fieldName of formElementFields) {
      const options = FormulaFields.ALL_FIELDS[fieldName];
      if (this.can(options.capability)) {
        this.defineProperty(`${fieldName}_formula`, null);
      }
    }

    this.defineProperty('rename_file', false);
    this.defineProperty('rename_file_to', "");

    this.loadFromJson(json);
    return this;
  }

  createEmbeddedObject = {
    form_elements: (parent, json): FormElement => {return this.rootModel.createFormElement(json.element_type);},
    security_maskable_full_viewers: (parent, json): SecurityMaskableViewer<FormElement> => {
      return new SecurityMaskableViewer(this, json, "security_maskable_full_viewers")
    },
    security_maskable_partial_viewers: (parent, json): SecurityMaskableViewer<FormElement> => {
      return new SecurityMaskableViewer(this, json, "security_maskable_partial_viewers")
    }
  };

  get isTemplateType(): boolean {
    const templateTypes = [
      FormElementType.thirdParty,
      FormElementType.panValidation,
      FormElementType.USPSAddress,
      FormElementType.MMCCProviderValidation,
    ];
    return templateTypes.includes(this.element_type);
  }

  get EMBEDDED_MODELS(): {[key: string]: {type: any, relationType: RelationType}} {
    const models = {
      'form_elements': {type: FormElement, relationType: RelationType.dictionary},
      'security_maskable_full_viewers': { type: SecurityMaskableViewer, relationType: RelationType.dictionary },
      'security_maskable_partial_viewers': { type: SecurityMaskableViewer, relationType: RelationType.dictionary }
    };
    for (const definition of FormElementConditionalFields.FORM_ELEMENT_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: FormElementConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }

    for (const definition of ObjectModelConditionalFields.VALIDATION_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  get EMBEDDED_HASHES(): string[] {
    return [...FormElementConditionalFields.FORM_ELEMENT_FIELDS, ...ObjectModelConditionalFields.VALIDATION_FIELDS]
      .filter(f => f.defaultConditionalValue instanceof Function)
      .map(f => f.fieldName);
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      'licenses',
    ];
  }

  can(duty: Capability): boolean {
    if (this.restrictedCapabilities && this.restrictedCapabilities.includes(duty)) {
      return false;
    }
    return FormElementCapabilityManager.formElementCan(this.element_type, duty);
  }

  /**
   * FormElement#can checks current state/configs to determine if element is capable
   * FormElement#canBe determines if there is ever a possibility the element could ever be capable
   *
   * This solves issue of grids not being Capability.referenceable on initial creation
   * since element.is_repeatable always starts as false but must be true to actually set the config
   */
  canBe(duty: Capability): boolean {
    if (this.restrictedCapabilities && this.restrictedCapabilities.includes(duty)) {
      return false;
    }
    switch (duty) {
      case Capability.referenceable:
        return FormElementCapabilityManager.formElementCanBe(this.element_type, duty);
      default:
        return this.can(duty)
    }
  }

  get canEditLock(): boolean {
    return this.inFormTemplate;
  }

  canConfigure(duty: Capability, property: typeof FormElementCapabilityManager.LOCKABLE_PROPERTIES[number]): boolean {
    // must have actual ability
    if (!this.can(duty)) {
      return false;
    }
    // must not be in elements template OR are in elements template and that property is not locked
    return !this.inElementsTemplate || (property && !this[`${property}_locked`]);
  }

  restrictionsIndex(element): number {
    return this.sort_order
  }

  get elementRestrictions(): Array<Array<number>> {
    return this._elementRestrictions;
  }

  get restrictedCapabilities(): number[] {
    let restrictions;
    if (this.isTemplateType) {
      restrictions = [];
    } else if ( this.inTemplate ) {
      let templateAncestor = this.templateAncestor as TemplateElement;
      if (templateAncestor && templateAncestor.isTemplateType) {
        if ( Object.keys(templateAncestor.restrictionSchema).length) {
          restrictions = this.restrictionsFromTemplate;
        }
      } else if (this.rootTemplate && this.rootTemplate.elementRestrictions) {
        restrictions = this.rootTemplate.elementRestrictions[this.rootTemplate.restrictionsIndex(this)];
      }
    }
    else if (this.parent && this.parent.elementRestrictions) {
      restrictions = this.parent.elementRestrictions[this.sort_order];
    }
    if (restrictions === undefined) {
      return [];
    } else {
      return restrictions;
    }
  }

  get restrictionsFromTemplate(): Array<Capability> {
    if (!this._restrictionsFromTemplate) {
      const templateParent = this.templateAncestor as any;
      const sortOrders = this.sortOrdersToTargetChild(this);
      let currentLevel = templateParent.restrictionSchema;
      sortOrders.forEach((sortOrder) => {
        currentLevel = currentLevel["children"][sortOrder];
      });
      let restrictions = currentLevel;
      this._restrictionsFromTemplate = restrictions["self"];
    }
    return this._restrictionsFromTemplate;
  }

  sortOrdersToParentTemplate(el): number[] {
    if (!this._sortOrdersToParentTemplate) {
      const sortOrders = [];
      let parent = this.parent;
      sortOrders.push(this.sort_order);
      while (!parent.isTemplate) {
        sortOrders.push(parent.sort_order);
        parent = parent.parent;
      }
      this._sortOrdersToParentTemplate = sortOrders;
    }
    return this._sortOrdersToParentTemplate;
  }

  sortOrdersToTargetChild(el): number[] {
    if (!this._sortOrdersToTargetChild) {
      this._sortOrdersToTargetChild = this.sortOrdersToParentTemplate(el).reverse();
    }
    return this._sortOrdersToTargetChild;
  }


  isRestricted(duty): boolean {
    return this.restrictedCapabilities.includes(duty);
  }

  afterMove(oldParent: ElementContainer, newParent: ElementContainer): void {}

  isTextComparisonType(): boolean {
    const validElementTypes = [FormElementType.textField, FormElementType.textArea];
    const invalidFormatTypes = [InputFormatType.Currency, InputFormatType.Numeric, InputFormatType.Percentage]
    return validElementTypes.includes(this.element_type) && !invalidFormatTypes.includes(this.format_type);
  }

  isSingleChoiceComparisonType(): boolean {
    const validElementTypes = [FormElementType.radioButtonGroup, FormElementType.dropdown];
    return validElementTypes.includes(this.element_type);
  }

  isMultiChoiceComparisonType(): boolean {
    const validElementTypes = [FormElementType.checkboxGroup];
    return validElementTypes.includes(this.element_type);
  }

  isNumericComparisonType(): boolean {
    const validFormatTypes = [InputFormatType.Currency, InputFormatType.Numeric, InputFormatType.Percentage]
    return validFormatTypes.includes(this.format_type)
  }

  isDatetimeComparisonType(): boolean {
    const validElementTypes = [FormElementType.dateTimePicker];
    return validElementTypes.includes(this.element_type);
  }

  isInstanceCountComparisonType(): boolean {
    return this.is_repeatable;
  }

  displayConditionType(targetType: TargetType, resetReferenceControl: boolean = false): ComparisonType {
    if (this.isInstanceCountComparisonType() || targetType === TargetType.count ) {
      return ComparisonType.instanceCount;
    } else if (this.isTextComparisonType()) {
      return ComparisonType.text;
    } else if (this.isSingleChoiceComparisonType()) {
      return ComparisonType.singleChoice;
    } else if (this.isMultiChoiceComparisonType()) {
      return ComparisonType.multiChoice;
    } else if (this.isNumericComparisonType()) {
      return ComparisonType.numeric;
    } else if (this.isDatetimeComparisonType()) {
      return ComparisonType.datetime;
    }
  }

  targetTypes(): TargetType[] {
    const types = [TargetType.value];
    if (this.isMultiChoiceComparisonType()) {
      types.push(TargetType.count);
    }
    return types;
  }

  get controlName(): string {
    return controlName(this as any)
  }

  get controlIcon(): string {
    return FormElementDefinition.getIcon(this.element_type);
  }

  isTypeOneOf(formElementTypes: Array<FormElementType>): boolean {
    return formElementTypes.includes(this.element_type);
  }


  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  get isLast(): boolean {
    return this.sort_order === (<ElementContainer>this.parent).nextChildSortOrder() - 1;
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      (<ElementContainer>this.parent).updateElementPositions(this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      (<ElementContainer>this.parent).updateElementPositions(this, this.sort_order);
    }
  }

  getConditionalRuleSets(fieldName: string): Hash<FormElementConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): FormElementConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  nextRuleSetSortOrder(fieldName: string): number {
    let position = 0;
    Object.keys(this.getConditionalRuleSets(fieldName))
      .map(key => this.getConditionalRuleSets(fieldName)[key])
      .forEach((element, i) => {
        if (i >= position) {
          position = i + 1;
        }
      });
    return position;
  }

  get rootName(): string {
    return 'form_elements';
  }

  get elementName(): string {
    return 'form_element';
  }

  get getConditionalRuleDependencies(): FormElementConditionalRuleSet[] {
    return this.rootModel.getConditionalRuleSetDependenciesFor(this.client_id);
  }

  removeConditionalRuleDependencies(): void {
    const ruleSets = this.getConditionalRuleDependencies;
    this.removeConditionalRules(ruleSets);
  }

  removeAllConditionalRules(): void {
    for (const {fieldName} of FormElementConditionalFields.FORM_ELEMENT_FIELDS) {
      const ruleSets = Object.keys(this.getConditionalRuleSets(fieldName)).map(key => this.getConditionalRuleSets(fieldName)[key]);
      this.removeConditionalRules(ruleSets);
    }
  }

  removeConditionalRule(ruleSet: FormElementConditionalRuleSet): void {
    ruleSet.repo.remove(ruleSet);
    const rules = ruleSet.conditionalRulesArray;
    for (let i = 0, len = rules.length; i < len; i++) {
      const rule = rules[i];
      if (rule instanceof ConditionalRule) {
        rule.repo.remove(rule);
      } else if (rule instanceof ConditionalRuleSet) {
        rule.repo.remove(rule);
      }
      rule.destroy();
    }
    ruleSet.destroy();
  }

  removeConditionalRules(ruleSets: FormElementConditionalRuleSet[]): void {
    for (let i = 0, len = ruleSets.length; i < len; i++) {
      this.removeConditionalRule(ruleSets[i]);
    }
  }

  get hasConditionalRuleDependencies(): boolean {
    return this.rootModel.getConditionalRuleSetDependenciesFor(this.client_id).length > 0;
  }

  get hasInvalidChildrenConditionRuleDependencies(): boolean {
    return this.getInvalidChildrenConditionRuleDependencies().length > 0;
  }

  getInvalidChildrenConditionRuleDependencies(ruleSets = []): FormElementConditionalRuleSet[] {
    this.eachDescendent(e => {
      this.rootModel.getConditionalRuleSetDependenciesFor(e.client_id).forEach(ruleSet => {
        if (!this.descendentClientIds.includes(ruleSet.parent.client_id) && !ruleSets.includes(ruleSet)) {
          ruleSets.push(ruleSet);
        }
      });
    });
    return ruleSets;
  }

  removeInvalidChildConditionalRuleDependencies(): void {
    const ruleSets = this.getInvalidChildrenConditionRuleDependencies();
    this.removeConditionalRules(ruleSets);
  }

  checkConditional(fieldName: string): boolean {
    return this[fieldName] ||
      (this[`${fieldName}_conditional_rule_sets`] && Object.keys(this[`${fieldName}_conditional_rule_sets`]).length > 0);
  }

  get elementType() {
    return this.element_type;
  }

  get elementTypeName(): string {
    switch (this.element_type) {
      case FormElementType.textField:
        return 'input field';
      case FormElementType.textArea:
        return 'text area';
      case FormElementType.dateTimePicker:
        return 'date/time picker';
      case FormElementType.dropdown:
        return 'dropdown';
      case FormElementType.checkboxGroup:
        return 'checkbox group';
      case FormElementType.radioButtonGroup:
        return 'radio button group';
      case FormElementType.checkbox:
        return 'checkbox';
      case FormElementType.radioButton:
        return 'radio button';
      case FormElementType.dropdownItem:
        return 'dropdown item';
      case FormElementType.uploadFiles:
        return 'upload files';
      case FormElementType.signature:
        return 'signature';
      case FormElementType.heading:
        return 'heading';
      case FormElementType.subheading:
        return 'subheading';
      case FormElementType.paragraph:
        return 'paragraph';
      case FormElementType.stack:
        return 'control stack';
      case FormElementType.grid:
        return 'grid';
      case FormElementType.pageBreak:
        return 'page break';
      case FormElementType.divider:
        return 'divider';
        case FormElementType.validator:
        return 'validator';
      case FormElementType.imageUpload:
        return 'image upload';
      default:
        return 'form element';
    }
  }

  get formSection(): FormSection {
    if (this.parent instanceof FormSection) {
      return this.parent;
    } else {
      return this.parent.formSection;
    }
  }

  get workflowStep(): WorkflowStep {
    return this.formSection.workflowStep;
  }

  get form(): Form {
    return this.formSection.form
  }

  get hasLabelTitle(): boolean {
    return this.label_title !== null && this.label_title.length > 0;
  }

  get ancestors(): FormElement[] {
    return Array.from(this.eachAncestor());
  }

  get repeatableAncestors(): FormElement[] {
    return this.ancestors.filter(a => a.is_repeatable);
  }

  get isInRepeatable(): boolean {
    return this.repeatableAncestors.length > 0
  }

  get containerScope(): FormSection | ElementContainer{
    return this.ancestors.find( (x)=> x instanceof FormSection || x instanceof ElementContainer)
  }

  * eachAncestor(): IterableIterator<FormElement> {
    let parent = this.parent;
    while (parent instanceof FormElement) {
      yield parent;
      parent = parent.parent;
    }
  }

  /**
   * Depth first
   */
  eachDescendent(callback: (element: IFormElement) => any): any {
    if (this.isEmpty) {
      return;
    }
    this.formElementsArray.forEach(e => {
      callback(e);
      e.eachDescendent(callback);
    });
  }

  get descendents(): IFormElement[] {
    const elements = []
    this.eachDescendent(e => elements.push(e))
    return elements
  }

  get descendentClientIds(): string[] {
    const ids = [];
    this.eachDescendent(e => ids.push(e.client_id));
    return ids;
  }

  afterDestroy() {
    super.afterDestroy();
    if (this.parent.element_type !== FormElementType.grid) {
      this.parent.recalculateElementPositions();
    }
    this.rootModel.removeElementIndex(this);
  }

  get defaultReferenceName(): string {
    return this.label_title || `Untitled ${this.controlName}`;
  }

  get referenceNameOrDefault(): string {
    return this.reference_name || this.defaultReferenceName;
  }

  get defaultLabelTitle(): string {
    return label(this as any)
  }

  get mappingLabelTitle(): string {
    return this.reference_name || this.label_title || `Untitled ${this.controlName}`;
  }

  get displayTitle(): string {
    return this.defaultLabelTitle
  }

  get formulaReferenceName(): string {
    return this.referenceNameOrDefault.replace(/ /g, "_");
  }

  get selectableFormElement(): FormElement {
    if (this.can(Capability.selectable)) {
      return this;
    } else if (this.parent && (this.parent instanceof FormElement)) {
      return (this.parent as FormElement).selectableFormElement;
    }
  }

  get selector(): typeof Selected['formElement'] {
    return Selected.formElement;
  }

  get isSelectable(): boolean {
    return this.can(Capability.selectable)
  }

  get isSelected(): boolean {
    return this.selector.is(this);
  }

  select(): void {
    if (this.isSelectable) {
      this.selector.set(this)
    }
  }

  deselect(): void {
    this.selector.deselectIf(this);
  }

  get itOrAncestorIsSelected(): boolean {
    const selectableFormElement = this.selectableFormElement;
    if (selectableFormElement) {
      return selectableFormElement.isSelected;
    }
    return false;
  }

  get isIndexable(): boolean {
    // must be indexable
    // must not re repeatable or in a repeatable
    return this.can(Capability.indexable) && !this.is_repeatable && !this.isInRepeatable;
  }

  get isIndexed(): boolean {
    return this.rootModel.indexed_element_ids.includes(this.client_id);
  }

  setIsRepeatable(val: boolean): void {
    this.is_repeatable = val;
    if (!this.isIndexable) {
      this.rootModel.removeElementIndex(this);
    }
  }

  /**
   * Crumbs for form elements plus workflow step
   */
  get breadcrumbs(): string[] {
    return [
      this.workflowStep.titleOrDefault,
      ...this.elementBreadcrumbs,
    ]
  }

  /**
   * Only contains crumbs for form elements
   */
  get elementBreadcrumbs(): string[] {
    return [
      ...this.ancestorElementBreadcrumbs,
      this.referenceNameOrDefault,
    ]
  }

  /**
   * Crumbs for ancestor form elements
   */
  get ancestorElementBreadcrumbs(): string[] {
    return this.ancestors.map(a => a.referenceNameOrDefault).reverse()
  }

  get isTemplate(): boolean {
    return this.element_type === FormElementType.template;
  }

  get rootTemplate(): FormElement {
    if (this.inTemplate){
      return this.parent.rootTemplate
    }else{
      return this
    }
  }

  get inElementsTemplate(): boolean {
    const iterator = this.eachAncestor();
    let result;
    do {
      result = iterator.next();
    } while (!result.done && !result.value.isTemplate);
    return !result.done;
  }

  get inFormTemplate(): boolean {
    return this.rootModel.isTemplate;
  }

  get inTemplate(): boolean {
    return this.inFormTemplate || this.inElementsTemplate;
  }

  get isThirdParty(): boolean {
    return this.element_type === FormElementType.thirdParty;
  }

  get isInThirdParty(): boolean {
    return this.parent.isThirdParty;
  }

  get templateAncestor(): FormElement {
    const iterator = this.eachAncestor();
    let result;
    do {
      result = iterator.next();
    } while (!result.done && !result.value.isTemplate);
    return result.value;
  }

  constructTemplate() {
    return;
  }

  get isSortable(): boolean {
    return this.can(Capability.sortable) && !this.inElementsTemplate;
  }

  get isDestroyable(): boolean {
    return this.can(Capability.destroyable) && !this.inElementsTemplate;
  }

  get isExpandable(): boolean {
    return this.can(Capability.layoutContainer) && !this.isThirdParty && !this.inElementsTemplate;
  }

  defaultNumericValidation(): void {
    if (this.hasOwnProperty('default_value')) {
      this.default_value = numericValidationWithOptionalDecimalPlaces(this.default_value, this)
    }
  }

  get datetimeMode(): DatetimeMode {
    return this.datetime_mode;
  }

  get isValidForConditionalLogic(): boolean {
    return true;
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): FormElementConditionalRuleSet | ObjectModelConditionalRuleSet {
    if (fieldName === "lookup_validation_rule" || fieldName === "show_validation_rule") {
      return new ObjectModelConditionalRuleSet(fieldName, definition);
    } else {
      return new FormElementConditionalRuleSet(fieldName, definition);
    }
  }

  getReferencedControl(id: string): FormElement | ObjectModelProperty {
    if (this.element_type === FormElementType.validator && this.validation_reference_type === ValidationReferenceType.objectModel && this.validationReferenceObjectModel) {
      return ObjectModelProperty.repo.find(id);
    } else {
      return FormElement.repo.find(id);
    }
  }

  getReferencableControls(): IConditionalRuleReferencedControl[] {
    if (this.element_type === FormElementType.validator && this.validation_reference_type === ValidationReferenceType.objectModel && this.validationReferenceObjectModel) {
      return this.validationReferenceObjectModel.objectModelPropertiesArray.filter(property => property.objectModel.isPropertyIndexed(property));
    } else {
      return this.getReferencableFormElements()
    }
  }

  getReferencableFormElements(): IFormElement[] {
    return this.rootModel.referenceableFormElementsFor(this);
  }

  getTargetReferencableControls(): IConditionalRuleReferencedControl[] {
    return this.getReferencableFormElements()
  }

  getChoiceComparisonOptions(): any[] {
    return ObjectHelper.toArray(this.form_elements);
  }

  getConditionDateTimeMode(): DatetimeMode {
    return this.datetime_mode;
  }

  getConditionMinDateTime(): string {
    return this.min_datetime;
  }

  getConditionMaxDateTime(): string {
    return this.max_datetime;
  }

  get modelType(): ModelType {
    return ModelType.FormElement
  }

  get objectModelSearchPropertyLabelProperties(): IObjectModelProperty[] {
    if (!this.object_model_search_result_label_property_ids || !this.object_model) {
      return null;
    }

    const propertiesById = _.keyBy(this.object_model.indexed_properties, p => p.id);

    // needs to preserve order of property_ids array
    return this.object_model_search_result_label_property_ids.map(id => propertiesById[id]);
  }

  isDefaultPlaceholder(): boolean {
    const DEFAULT_PLACEHOLDERS: string[] = ['MM/DD/YYYY HH:MM', 'MM/DD/YYYY', 'HH:MM'];
    return DEFAULT_PLACEHOLDERS.includes(this.placeholder_text)
  }

  setPlaceholderText(newVal: number, reset: boolean = false) {
    if (this.hasOwnProperty('placeholder_text') && (this.isDefaultPlaceholder() || reset)) {
      switch (newVal) {
        case DatetimeMode.Date:
          this.placeholder_text = 'MM/DD/YYYY';
          break;
        case DatetimeMode.Time:
          this.placeholder_text = 'HH:MM';
          break;
        case DatetimeMode.Datetime:
          this.placeholder_text = 'MM/DD/YYYY HH:MM';
          break;
      }
    }
  }

  get validationReferenceObjectModel() {
    if (this.validation_reference_object_model_id) {
      return ObjectModel.repo.findBy( om => om.id === this.validation_reference_object_model_id)
    }
  }

  formElementsForFormula({includeRepeatables}: { includeRepeatables?: boolean }): FormElement[] {
    return this.formSection.formElementsForFormula({includeRepeatables})
  }

  /**
   * if the data of interest is itself, a collection of data
   */
  get isDataCollection(): boolean {
    return (this.can(Capability.repeatable) && this.is_repeatable) ||
      this.elementType === FormElementType.checkboxGroup ||
      (this.elementType === FormElementType.uploadFiles && this.is_multiple)
  }

  /**
   * if this element is a group of data by any means - itself is a collection of things or is contained in a repeatable
   */
  get isCollection(): boolean {
    return this.isInRepeatable || this.isDataCollection
  }

  get isUploader(): boolean {
    return this.elementType === FormElementType.uploadFiles || this.elementType === FormElementType.imageUpload;
  }

  get isPickList(): boolean {
    return FormElementCapabilityManager.OPTION_COLLECTION_CONTROLS.includes(this.element_type)
  }

  get isDateTime(): boolean {
    return this.element_type === FormElementType.dateTimePicker;
  }

  get isSignature(): boolean {
    return this.element_type === FormElementType.signature;
  }

  get isLookupField(): boolean {
    return this.element_type === FormElementType.lookupField;
  }

  get isCheckboxGroup(): boolean {
    return this.element_type === FormElementType.checkboxGroup;
  }

  get isCurrencyFormat(): boolean {
    return this.can(Capability.formatable) && this.format_type === InputFormatType.Currency;
  }

  get isNumeric(): boolean {
    return this.genericType === ModelType.Number
  }

  get selectionType(): PickListSelectionType {
    if (!FormElementCapabilityManager.OPTION_COLLECTION_CONTROLS.includes(this.element_type)) return null
    return this.isDataCollection ? PickListSelectionType.MultiSelect : PickListSelectionType.SingleSelect
  }

  get genericType(): GenericType {
    return FormElementDefinition.genericType(this)
  }

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.FormElement
  }


  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter(this.imposterMapFor({ modelType, options }))
  }

  imposterMapFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposterMap {
    let source: {modelType: ModelType, genericType: GenericType} = {modelType: this.modelType, genericType: this.genericType}
    if (this.format_type === InputFormatType.Email) {
      if (modelType === ModelType.User) {
        source.modelType = ModelType.User
      } else {
        source.modelType = ModelType.Email
      }
    }
    let map: IImposterMap
    if (this.element_type === FormElementType.lookupField) {
      if (modelType === ModelType.ObjectModel && this.lookup_type === LookupFieldType.objectModels) {
        map = ObjectHelper.merge(
          new DefaultImposterMapFactory().imposterMapFor({ modelType, options }),
          {
            id: this.object_model_id,
          },
        )
      }
    } else if (this.element_type === FormElementType.signature) {
      map = {
        id: null,
        genericType: ModelType.Signature,
        modelType: ModelType.FormElement,
        isCollection: null,
      }
    }

    if (!map) {
      map = commonImposterMap({
          destination: { modelType, genericType: options.genericType },
          source,
          options,
        },
      )
    }

    if (!map) {
      map = new DefaultImposterMapFactory().imposterMapFrom(this)
    }

    if (this.format_type === InputFormatType.Email) {
      ObjectHelper.merge(
        map,
        {
          modelType: ModelType.Email,
          id: null
        },
      )
    }

    if ((modelType === ModelType.User) && this.format_type === InputFormatType.Email) {
      ObjectHelper.merge(
        map,
        {
          id: null,
          genericType: ModelType.Object
        },
      )
    }


    /**
     * Only tack on +isCollection+ from the form element if the key is not already provided as an override
     * Useful for process flow "grid" variable where the grid itself is repeatable in the form but the variable is
     * representing a single instance of it
     * and always take into account is the data itself is a collection
     */
    if (options.hasOwnProperty('isCollection')) {
      map.isCollection = options.isCollection || this.isDataCollection
    } else {
      map.isCollection = this.isCollection
    }

    return map
  }

  /**
   *
   * @param value {String} client_id of pick list option or text value for other property types
   * @param args
   */
  toUniversalValue(value: any, ...args: any[]) {
    if (this.isPickList) {
      const getLabel = (clientId: string): string => this.form_elements[clientId]?.defaultLabelTitle
      return InstanceHelper.actOn(InstanceHelper.toArray(value), getLabel)
    } else {
      return value
    }
  }

  /**
   *
   * @param value {String} label_title of pick list option or text value for other property types
   */
  fromUniversalValue(value: any) {
    if (this.isPickList) {
      const getClientId = (labelTitle: string): string => this.formElementsArray.find(e => e.label_title === labelTitle)?.client_id
      return InstanceHelper.actOn(InstanceHelper.toArray(value), getClientId)
    } else {
      return value
    }
  }
}